import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useUpdateWalletLimits, useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import {
  notificationsActions,
  sellActions,
  useStore,
  walletActions,
  transactionsActions,
} from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { GETSellRateResponse } from '@modules/sell/types';
import { ProtectNextEnum } from '@modules/types';
import { OperationsEnum } from '@modules/wallet/types';

import Button from '@components/Button';
import Converter, { TConverter } from '@containers/Converter';
import Limits from '@containers/Converter/Limits';
import useConverterState from '@containers/Converter/useConverterState';
import ZHDisclaimerInfo from '@containers/ZHDisclaimerInfo';

import Details from '../../../Details';
import OperationsDisabledDisclaimer from '../../OperationsDisabledDisclaimer';
import { useCustomContext } from '../../context';

import style from './Sell.scss';

const cn = bemCssModules(style, 'Sell');
const ti = getTestId('Sell');
const { verifyOTP, setupOTP } = ProtectNextEnum;

const Sell: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const [{ global, sell, wallet }, dispatch] = useStore();
  const { currencies } = global;
  const { left, right } = useCustomContext();
  const navigate = useNavigate();

  const location = useLocation<LocationStateType['wallets']>();
  const { state } = location;
  const { cacheConverter } = state || {};

  const { setSelectedCurrency } = useCustomContext();

  const converterState = useConverterState({
    fromCurrencies: [left?.value as CryptoCurrencyEnum],
    toCurrencies: currencies.fiat.filter(c => CONST.sellCurrencies.includes(c)),
    cacheState: cacheConverter,
  });
  const {
    fromCurrency,
    toCurrency,
    rate,
    fromAmount,
    toAmount,
    showLimits,
    setFromAmount,
    setToAmount,
    updateRates,
  } = converterState;
  const sell_token = (rate as GETSellRateResponse)?.sell_token;

  useEffect(() => {
    if (state.cacheConverter) state.cacheConverter = false;
  }, []);

  useEffect(() => {
    if (fromCurrency) setSelectedCurrency(fromCurrency);
  }, [fromCurrency]);

  const getRate: TConverter['getRate'] = (from, to, amount) =>
    dispatch(sellActions.getSellRate({ from, to, amount })).unwrap();

  useUpdateWalletLimits({
    crypto_currency: fromCurrency,
    currency: toCurrency,
    type: OperationsEnum.sell,
    shouldStop: !showLimits,
  });

  useEffect(() => {
    if (fromAmount || toAmount) updateRates();
  }, [right?.value, left.value]);

  const onSubmit = () => {
    if (CONST.operationsDisabled) return;
    if (fromCurrency && right && toCurrency) {
      const data = { card_id: right?.value, sell_token };
      const totalWithCurrency = `${rate?.total[fromCurrency]} ${fromCurrency}`;

      dispatch(sellActions.sellAction(data))
        .unwrap()
        .then(res => {
          const message = t('notifications.order_processed', { totalWithCurrency });
          if (res.data.next === verifyOTP) {
            navigate<'verifications'>(CONST.nav.verification, {
              state: {
                nextPage: CONST.nav.root,
                flow: 'SellVerifyOTP',
                sellData: data,
                value: totalWithCurrency,
              },
            });
          } else {
            dispatch(notificationsActions.add({ message }));
            dispatch(transactionsActions.getTransactions());
          }
        })
        .catch(err => {
          if (err.data.next === setupOTP) {
            navigate(CONST.nav.intro2fa);
          } else if (err.data.next === verifyOTP) {
            navigate<'verifications'>(CONST.nav.verification, {
              state: {
                nextPage: CONST.nav.root,
                flow: 'SellVerifyOTP',
                sellData: data,
                value: totalWithCurrency,
              },
            });
          }
          return err;
        })
        .finally(() => {
          dispatch(walletActions.getWallet())
            .unwrap()
            .then(res => {
              const limits = wallet.limits.sell[fromCurrency as CryptoCurrencyEnum];
              const minLimit = Number(limits?.min);
              const maxLimit = Number(limits?.max);
              const balance = +res.data[fromCurrency as CryptoCurrencyEnum]?.balance;
              const isValidBalance = balance >= minLimit && balance <= maxLimit;
              if (isValidBalance) updateRates();
              else {
                setFromAmount('');
                setToAmount('');
              }
            });
        });
    }
  };

  const disabled = CONST.operationsDisabled || !+fromAmount || !+toAmount || !sell_token;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <Converter getRate={getRate} {...converterState} />
      <Limits limits={wallet.limits.sell} {...converterState} />
      <Details from={fromCurrency} to={toCurrency} operationRate={rate} withAdditionalFee />
      <Button
        className={cn('button')}
        onClick={onSubmit}
        isLoading={sell.loaders.sellAction}
        disabled={disabled}
        testId={ti('button')}
      >
        {t('card.sell')}
      </Button>
      {CONST.operationsDisabled && <OperationsDisabledDisclaimer />}
      <ZHDisclaimerInfo />
    </div>
  );
};

export default Sell;
